import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import {
  ackInventoryUploadsForClaim,
  getInventoryUploadsDashboard,
  InventoryUploadGroup,
} from '../services/inventoryUploadsApi';
import { brandColors } from '../styles/brandColors';
import DashboardClaimGroupCard from './dashboard/DashboardClaimGroupCard';

const tileColors = {
  shell: '#f6f8fc',
  header: 'linear-gradient(135deg, #1f4d2d 0%, #2f6b3a 55%, #3f8750 100%)',
  headerIcon: 'rgba(255, 255, 255, 0.16)',
  border: 'rgba(31, 77, 45, 0.14)',
  text: '#1e293b',
};

const uploadCountLabel = (count: number): string =>
  count === 1 ? '1 upload' : `${count} uploads`;

const DashboardInventoryUploads: React.FC = () => {
  const [groups, setGroups] = useState<InventoryUploadGroup[]>([]);
  const [newCount, setNewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clearingClaimId, setClearingClaimId] = useState<number | null>(null);

  const loadUploads = useCallback(async () => {
    setError('');
    try {
      const data = await getInventoryUploadsDashboard();
      setGroups(Array.isArray(data.groups) ? data.groups : []);
      setNewCount(data.new_count ?? 0);
    } catch {
      setError('Unable to load inventory uploads.');
      setGroups([]);
      setNewCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUploads();
  }, [loadUploads]);

  const handleClearAll = async (claimId: number, uploadCount: number) => {
    setClearingClaimId(claimId);
    try {
      await ackInventoryUploadsForClaim(claimId);
      setGroups((prev) => prev.filter((group) => group.claim_id !== claimId));
      setNewCount((count) => Math.max(0, count - uploadCount));
    } catch {
      setError('Unable to clear uploads for this claim.');
    } finally {
      setClearingClaimId(null);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${tileColors.border}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        height: '100%',
        minHeight: 260,
        backgroundColor: tileColors.shell,
        boxShadow: '0 10px 28px rgba(0, 21, 69, 0.1)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          px: 2.5,
          py: 2,
          background: tileColors.header,
          borderBottom: `1px solid ${tileColors.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: tileColors.headerIcon,
              color: '#fff',
              flexShrink: 0,
            }}
          >
            <UploadFileOutlinedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.2, fontSize: '1.05rem' }}>
              Uploads
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.84)', display: 'block', mt: 0.35 }}>
              New inventory receipts and evidence files
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.25,
            py: 0.5,
            borderRadius: 999,
            backgroundColor: newCount > 0 ? 'rgba(248, 113, 113, 0.28)' : 'rgba(255, 255, 255, 0.14)',
            color: '#fff',
            flexShrink: 0,
            border: newCount > 0 ? '1px solid rgba(255,255,255,0.2)' : '1px solid transparent',
          }}
        >
          <UploadFileOutlinedIcon sx={{ fontSize: 15 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '0.75rem' }}>
            {newCount} New
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          backgroundColor: tileColors.shell,
          p: 1.5,
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography color="error" variant="body2" sx={{ p: 1.5 }}>
            {error}
          </Typography>
        ) : groups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                mx: 'auto',
                mb: 1.25,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#e7efe8',
              }}
            >
              <UploadFileOutlinedIcon sx={{ fontSize: 24, color: 'rgba(31, 77, 45, 0.45)' }} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: brandColors.navy }}>
              No new inventory uploads
            </Typography>
          </Box>
        ) : (
          groups.map((group) => (
            <DashboardClaimGroupCard
              key={group.claim_id}
              claimId={group.claim_id}
              claimNumber={group.claim_number}
              facilityId={group.facility_id}
              tenantName={group.tenant_name}
              statusLabel={group.status_label}
              timestamp={group.latest_upload_at}
              onClearAll={() => handleClearAll(group.claim_id, group.upload_count)}
              clearing={clearingClaimId === group.claim_id}
              clearAllTooltip="Mark all uploads for this claim as reviewed"
              theme="uploads"
            >
              <Typography
                variant="body2"
                component="p"
                sx={{
                  color: tileColors.text,
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  lineHeight: 1.5,
                  m: 0,
                }}
              >
                {uploadCountLabel(group.upload_count)}
              </Typography>
            </DashboardClaimGroupCard>
          ))
        )}
      </Box>
    </Paper>
  );
};

export default DashboardInventoryUploads;
